import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Download, BookOpen, ChevronDown, Phone, MapPin, Clock, Globe, Loader2, ArrowLeft, ArrowRight, Sparkles, PartyPopper, Monitor, Copy, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { cloudFunctions } from '@/lib/firebase';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { GlassCard } from '@/components/ui/GlassCard';
import { Button } from '@/components/ui/Button';

// Check if device is mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(mobileRegex.test(userAgent) || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

interface ContactInfo {
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  hours: string;
  socialFacebook: string;
  socialInstagram: string;
  socialTwitter: string;
}

interface DayHours {
  enabled: boolean;
  open: string;
  close: string;
}

type WeekHours = {
  [key: string]: DayHours;
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIME_OPTIONS = [
  '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM',
  '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM', '11:00 PM',
];

const DEFAULT_HOURS: WeekHours = {
  Monday: { enabled: true, open: '9:00 AM', close: '5:00 PM' },
  Tuesday: { enabled: true, open: '9:00 AM', close: '5:00 PM' },
  Wednesday: { enabled: true, open: '9:00 AM', close: '5:00 PM' },
  Thursday: { enabled: true, open: '9:00 AM', close: '5:00 PM' },
  Friday: { enabled: true, open: '9:00 AM', close: '5:00 PM' },
  Saturday: { enabled: false, open: '10:00 AM', close: '2:00 PM' },
  Sunday: { enabled: false, open: '10:00 AM', close: '2:00 PM' },
};

function formatWeekHoursToString(weekHours: WeekHours): string {
  const lines: string[] = [];
  for (const day of DAYS) {
    const h = weekHours[day];
    if (h.enabled) {
      lines.push(`${day.slice(0, 3)}: ${h.open} - ${h.close}`);
    } else {
      lines.push(`${day.slice(0, 3)}: Closed`);
    }
  }
  return lines.join(', ');
}

export function PurchaseSuccessPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [siteName, setSiteName] = useState<string>('');
  const [, setCustomizationComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [, setDownloadComplete] = useState(false);
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Wizard steps: 0 = welcome, 1 = contact, 2 = address, 3 = hours, 4 = social, 5 = done
  const [wizardStep, setWizardStep] = useState(0);

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    hours: '',
    socialFacebook: '',
    socialInstagram: '',
    socialTwitter: '',
  });

  const [weekHours, setWeekHours] = useState<WeekHours>(DEFAULT_HOURS);

  const token = searchParams.get('token');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    async function completePurchase() {
      if (!token) {
        setError('Invalid purchase link');
        setLoading(false);
        return;
      }

      try {
        const isReturning = sessionId === 'returning';

        if (!isReturning && sessionId) {
          await cloudFunctions.completeSitePurchase({ sessionId, token });
        }

        const status = await cloudFunctions.getSiteCustomizationStatus({ token });
        setSiteName(status.name);
        setCustomizationComplete(status.customizationComplete);

        if (status.customizationComplete) {
          setWizardStep(5);
        }

        if (status.buyerContactInfo) {
          const info = status.buyerContactInfo as Record<string, string>;
          setContactInfo((prev) => ({
            ...prev,
            phone: info.phone || '',
            email: info.email || '',
            address: info.address || '',
            city: info.city || '',
            state: info.state || '',
            zip: info.zip || '',
            hours: info.hours || '',
            socialFacebook: info.socialFacebook || '',
            socialInstagram: info.socialInstagram || '',
            socialTwitter: info.socialTwitter || '',
          }));
        }
      } catch (err) {
        console.error('Error completing purchase:', err);
        setError('Failed to load purchase. Please contact support.');
      } finally {
        setLoading(false);
      }
    }

    completePurchase();
  }, [token, sessionId]);

  const handleContactInfoChange = (field: keyof ContactInfo, value: string) => {
    setContactInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (day: string) => {
    setWeekHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day].enabled },
    }));
  };

  const handleTimeChange = (day: string, field: 'open' | 'close', value: string) => {
    setWeekHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const handleSubmitContactInfo = async () => {
    if (!token) return;

    if (!contactInfo.email) {
      toast.error('Email is required for your contact form to work');
      return;
    }

    setIsSubmitting(true);
    try {
      const hoursString = formatWeekHoursToString(weekHours);
      await cloudFunctions.updateBuyerContactInfo({
        token,
        contactInfo: { ...contactInfo, hours: hoursString },
      });
      setCustomizationComplete(true);
      setWizardStep(5);
      toast.success('Setup complete! You can now download your site.');
    } catch (err) {
      console.error('Error saving contact info:', err);
      toast.error('Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!token) return;

    setIsDownloading(true);
    try {
      const result = await cloudFunctions.downloadSiteZip({ token });

      const binaryString = atob(result.zipBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDownloadComplete(true);
      toast.success('Download started!');
    } catch (err) {
      console.error('Error downloading:', err);
      toast.error('Failed to download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const canProceed = () => {
    switch (wizardStep) {
      case 1: return !!contactInfo.email;
      case 2: return true; // Address is optional
      case 3: return true; // Hours are pre-filled
      case 4: return true; // Social is optional
      default: return true;
    }
  };

  const handleNext = () => {
    if (wizardStep < 4) {
      setWizardStep(wizardStep + 1);
    } else {
      handleSubmitContactInfo();
    }
  };

  const handleBack = () => {
    if (wizardStep > 0) {
      setWizardStep(wizardStep - 1);
    }
  };

  if (loading) {
    return <LoadingScreen message="LOADING YOUR WEBSITE (MADE WITH ROMULUS)..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <GlassCard className="text-center max-w-md w-full">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-4">Something Went Wrong</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </GlassCard>
      </div>
    );
  }

  const currentUrl = window.location.href;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      toast.success('Link copied!');
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  // Mobile view - show "open on desktop" message
  if (isMobile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-6">
        <div className="text-center max-w-sm">
          <div
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
          >
            <Monitor className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-3">Open on Desktop</h1>

          <p className="text-gray-400 mb-6">
            To complete your website setup and download your files, please open this link on a desktop or laptop computer.
          </p>

          <div
            className="p-4 rounded-xl mb-6"
            style={{
              background: 'rgba(91, 58, 58, 0.1)',
              border: '1px solid rgba(91, 58, 58, 0.2)',
            }}
          >
            <p className="text-xs text-gray-400 mb-3">Copy this link:</p>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
              <p className="text-white text-xs break-all flex-1 text-left">
                {currentUrl.length > 50 ? currentUrl.slice(0, 50) + '...' : currentUrl}
              </p>
            </div>
          </div>

          <Button
            onClick={handleCopyLink}
            className="w-full"
            style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
          >
            <span className="flex items-center justify-center gap-2">
              {copied ? (
                <>
                  <Check className="w-5 h-5" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  Copy Link
                </>
              )}
            </span>
          </Button>

          <p className="text-gray-500 text-xs mt-6">
            The setup process requires a desktop browser to download and configure your website files.
          </p>
        </div>
      </div>
    );
  }

  // Render wizard step content
  const renderWizardContent = () => {
    switch (wizardStep) {
      case 0:
        return (
          <div className="text-center px-4">
            <div
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
            >
              <PartyPopper className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">Congratulations!</h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-2">
              Your website for <span className="text-white font-semibold">{siteName}</span> is ready.
            </p>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              We just need a few details to finalize your site. This only takes about 2 minutes.
            </p>

            <div
              className="p-4 rounded-xl mb-8 mx-auto max-w-sm"
              style={{
                background: 'rgba(91, 58, 58, 0.1)',
                border: '1px solid rgba(91, 58, 58, 0.2)',
              }}
            >
              <Sparkles className="w-5 h-5 text-[#B08080] mx-auto mb-2" />
              <p className="text-sm text-gray-300">
                Your custom website is already built and waiting. We'll add your contact info and you'll be ready to go live!
              </p>
            </div>

            <Button
              onClick={() => setWizardStep(1)}
              className="w-full max-w-xs mx-auto"
              style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
            >
              <span className="flex items-center justify-center gap-2">
                Let's Get Started
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </div>
        );

      case 1:
        return (
          <div className="px-4 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div
                className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
              >
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Contact Information</h2>
              <p className="text-gray-400 text-sm sm:text-base">
                How should customers reach you?
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  placeholder="hello@yourbusiness.com"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                />
              </div>
              <p className="text-xs text-gray-500 text-center">* Email is required for your site's contact form</p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="px-4 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div
                className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
              >
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Business Address</h2>
              <p className="text-gray-400 text-sm sm:text-base">
                Where is your business located? (Optional)
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Street Address</label>
                <input
                  type="text"
                  value={contactInfo.address}
                  onChange={(e) => handleContactInfoChange('address', e.target.value)}
                  placeholder="123 Main Street"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">City</label>
                  <input
                    type="text"
                    value={contactInfo.city}
                    onChange={(e) => handleContactInfoChange('city', e.target.value)}
                    placeholder="City"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">State</label>
                  <input
                    type="text"
                    value={contactInfo.state}
                    onChange={(e) => handleContactInfoChange('state', e.target.value)}
                    placeholder="State"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                  />
                </div>
              </div>
              <div className="w-1/2">
                <label className="block text-sm text-gray-400 mb-2">ZIP Code</label>
                <input
                  type="text"
                  value={contactInfo.zip}
                  onChange={(e) => handleContactInfoChange('zip', e.target.value)}
                  placeholder="12345"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="px-4 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div
                className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
              >
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Business Hours</h2>
              <p className="text-gray-400 text-sm sm:text-base">
                When are you open? Toggle days on/off.
              </p>
            </div>

            <div className="space-y-3">
              {DAYS.map((day) => (
                <div
                  key={day}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10"
                >
                  <button
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                      weekHours[day].enabled ? 'bg-[#B08080]' : 'bg-gray-600'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                        weekHours[day].enabled ? 'translate-x-[18px]' : 'translate-x-[2px]'
                      }`}
                    />
                  </button>
                  <span className="text-white font-medium w-16 sm:w-20 text-sm">{day.slice(0, 3)}</span>
                  {weekHours[day].enabled ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <select
                        value={weekHours[day].open}
                        onChange={(e) => handleTimeChange(day, 'open', e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1.5 rounded bg-white/10 border border-white/10 text-white text-sm focus:border-[#B08080] focus:outline-none"
                      >
                        {TIME_OPTIONS.map((t) => (
                          <option key={t} value={t} className="bg-gray-900">{t}</option>
                        ))}
                      </select>
                      <span className="text-gray-400 text-xs">to</span>
                      <select
                        value={weekHours[day].close}
                        onChange={(e) => handleTimeChange(day, 'close', e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1.5 rounded bg-white/10 border border-white/10 text-white text-sm focus:border-[#B08080] focus:outline-none"
                      >
                        {TIME_OPTIONS.map((t) => (
                          <option key={t} value={t} className="bg-gray-900">{t}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm">Closed</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="px-4 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div
                className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
              >
                <Globe className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Social Media</h2>
              <p className="text-gray-400 text-sm sm:text-base">
                Add your social links (Optional)
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Facebook</label>
                <input
                  type="url"
                  value={contactInfo.socialFacebook}
                  onChange={(e) => handleContactInfoChange('socialFacebook', e.target.value)}
                  placeholder="https://facebook.com/yourbusiness"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Instagram</label>
                <input
                  type="url"
                  value={contactInfo.socialInstagram}
                  onChange={(e) => handleContactInfoChange('socialInstagram', e.target.value)}
                  placeholder="https://instagram.com/yourbusiness"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Twitter / X</label>
                <input
                  type="url"
                  value={contactInfo.socialTwitter}
                  onChange={(e) => handleContactInfoChange('socialTwitter', e.target.value)}
                  placeholder="https://x.com/yourbusiness"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#B08080] focus:outline-none text-base"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="px-4 max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
              >
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">You're All Set!</h1>
              <p className="text-gray-400">
                Your website is ready. Download it and follow the guide to go live.
              </p>
            </div>

            {/* Download Section */}
            <GlassCard className="mb-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-[#B08080]" />
                Download Your Website
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                Get your website as a ZIP file ready to deploy.
              </p>
              <Button
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full"
                style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
              >
                {isDownloading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Preparing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Download className="w-5 h-5" />
                    Download ZIP
                  </span>
                )}
              </Button>
            </GlassCard>

            {/* Contact Form Activation */}
            <GlassCard className="mb-6">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#B08080]" />
                Activate Your Contact Form
              </h2>
              <p className="text-gray-400 text-sm mb-3">
                We've sent an activation request to <span className="text-white font-medium">{contactInfo.email}</span>. Check your inbox for an email from FormSubmit and click the <span className="text-white font-medium">"Activate Form"</span> link.
              </p>
              <div className="p-3 rounded-lg bg-[#B08080]/10 border border-[#B08080]/20">
                <p className="text-xs text-gray-400">
                  Your contact form won't receive messages until activated. Check your spam folder if you don't see the email within a few minutes.
                </p>
              </div>
            </GlassCard>

            {/* Deployment Guide */}
            <GlassCard>
              <button
                onClick={() => setShowGuide(!showGuide)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#B08080]" />
                  Deployment Guide
                </h2>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    showGuide ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {showGuide && (
                <div className="mt-6 space-y-4">
                  <p className="text-gray-400 text-sm">
                    Follow these steps to get your website live. Both options are free!
                  </p>

                  {/* Netlify Guide */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2 text-sm sm:text-base">
                      <span className="w-6 h-6 bg-[#00AD9F] rounded text-white text-xs flex items-center justify-center font-bold flex-shrink-0">N</span>
                      Option 1: Netlify (Recommended)
                    </h3>
                    <ol className="space-y-2 text-gray-400 text-xs sm:text-sm">
                      <li className="flex gap-2">
                        <span className="text-[#B08080] font-bold">1.</span>
                        <span>Go to <a href="https://app.netlify.com" target="_blank" rel="noopener noreferrer" className="text-[#B08080] underline">app.netlify.com</a> and sign up</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-[#B08080] font-bold">2.</span>
                        <span>Click "Add new site" → "Deploy manually"</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-[#B08080] font-bold">3.</span>
                        <span>Unzip and drag the folder onto upload area</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-[#B08080] font-bold">4.</span>
                        <span>Your site is live with free .netlify.app domain!</span>
                      </li>
                    </ol>
                  </div>

                  {/* Vercel Guide */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <h3 className="font-semibold text-white mb-3 flex items-center gap-2 text-sm sm:text-base">
                      <span className="w-6 h-6 bg-black border border-white rounded text-white text-xs flex items-center justify-center font-bold flex-shrink-0">▲</span>
                      Option 2: Vercel
                    </h3>
                    <ol className="space-y-2 text-gray-400 text-xs sm:text-sm">
                      <li className="flex gap-2">
                        <span className="text-[#B08080] font-bold">1.</span>
                        <span>Go to <a href="https://vercel.com" target="_blank" rel="noopener noreferrer" className="text-[#B08080] underline">vercel.com</a> and sign up</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-[#B08080] font-bold">2.</span>
                        <span>Click "Add New" → "Project" → "Upload"</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-[#B08080] font-bold">3.</span>
                        <span>Upload your unzipped folder and deploy</span>
                      </li>
                    </ol>
                  </div>

                  {/* Tips */}
                  <div className="p-4 rounded-xl bg-[#B08080]/10 border border-[#B08080]/20">
                    <h3 className="font-semibold text-white mb-2 text-sm">💡 Tips</h3>
                    <ul className="space-y-1 text-gray-400 text-xs sm:text-sm">
                      <li>• Both offer free SSL (https) automatically</li>
                      <li>• Update your site anytime by re-uploading</li>
                    </ul>
                  </div>
                </div>
              )}
            </GlassCard>

            {/* Preview Link */}
            <div className="mt-6 text-center">
              <Link
                to={`/p/${token}`}
                className="text-[#B08080] hover:underline text-sm"
              >
                View Website Preview →
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black px-4 flex flex-col">
      {/* Built With ROMVLVS branding - top right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 z-10">
        <span className="text-gray-500 text-xs">Built With</span>
        <div className="flex items-center gap-1.5">
          <img src="/wolfromulus.png" alt="Romulus" className="w-5 h-5 object-contain" />
          <span className="text-white font-roman tracking-widest text-sm font-bold">ROMVLVS</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto flex-1 flex flex-col justify-center py-8">
        {/* Progress indicator for wizard steps 1-4 */}
        {wizardStep > 0 && wizardStep < 5 && (
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`h-2 rounded-full transition-all ${
                    step <= wizardStep ? 'bg-[#B08080] w-8' : 'bg-white/20 w-4'
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-gray-500 text-xs sm:text-sm">
              Step {wizardStep} of 4
            </p>
          </div>
        )}

        {/* Wizard content */}
        <div className="mb-8">
          {renderWizardContent()}
        </div>

        {/* Navigation buttons for wizard steps 1-4 */}
        {wizardStep > 0 && wizardStep < 5 && (
          <div className="flex gap-3 max-w-md mx-auto px-4">
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting}
              className="flex-1"
              style={{ background: canProceed() ? 'linear-gradient(135deg, #B08080, #c49494)' : undefined }}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : wizardStep === 4 ? (
                <>
                  Finish Setup
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
